import { useState, useEffect } from "react";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { PasswordInput } from "@/project/karuna/components/ui/password-input";
import { Label } from "@/project/karuna/components/ui/label"; // @ts-ignore - label is not a valid module
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { useNavigate } from "react-router-dom";
import { Shield, ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { z } from "zod";
import navigationRoutes, {
  useKarunaLink,
} from "@/project/karuna/utils/navigationRoutes";
import AuthService from "@/services/APIs/AuthService";
import {
  getAccessToken,
  getUserInfo,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
  clearAuthData,
} from "@/lib/storeManager";
import {
  fetchAndStoreAdminPermissions,
  isAdminPortalUser,
} from "@/project/karuna/utils/adminPermissionUtils";

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }),
  password: z.string().min(1, { message: "Password is required" }),
});

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const getLink = useKarunaLink();

  useEffect(() => {
    const token = getAccessToken();
    const userInfo = getUserInfo();
    if (token && isAdminPortalUser(userInfo)) {
      navigate(getLink("/admin/dashboard"));
    }
  }, [navigate, getLink]);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form data
      const validated = loginSchema.parse(formData);

      console.log("🔐 Attempting admin login via /api/auth/login/...");
      console.log("📧 Email:", validated.email);

      // Call the actual API endpoint
      const response = await AuthService.login({
        email: validated.email.trim(),
        password: validated.password,
      });

      // Check if response is valid
      if (!response?.access) {
        throw new Error("Invalid response from server. Please try again.");
      }

      console.log("✅ Admin login successful");
      console.log("🔑 Access token received");
      console.log("👤 User:", response.user);

      // Store authentication tokens
      if (response.access) {
        setLocalAccessToken(response.access);
      }
      if (response.refresh) {
        setLocalRefreshToken(response.refresh);
      }

      // Store user info
      if (response.user) {
        const user = response.user as {
          first_name: string;
          last_name: string;
          email: string;
          user_type: string;
          phone_number?: string;
        };

        if (!isAdminPortalUser(user)) {
          clearAuthData();
          throw new Error(
            "This account is not authorized for the admin portal."
          );
        }

        setLocalUserInfo({
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          user_type: user.user_type,
          phone_number: user.phone_number,
        });
      }

      await fetchAndStoreAdminPermissions();

      toast({
        title: "Login Successful",
        description: "Welcome to the admin portal",
      });

      // Navigate to admin dashboard
      navigate(getLink("/admin/dashboard"));
    } catch (error: any) {
      console.error("❌ Admin login error:", error);

      // Handle validation errors
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.issues[0]?.message ?? "Please check your input",
          variant: "destructive",
        });
      } else {
        // Handle API errors
        const errorMessage =
          error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          error?.response?.data?.error ??
          error?.message ??
          "Invalid credentials. Please check your email and password.";

        toast({
          title: "Login Failed",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(getLink(navigationRoutes.DEMO_LOGIN))}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>

        {/* Login Card */}
        <Card className="shadow-xl">
          <CardHeader className="space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70">
              <Shield className="h-8 w-8 text-primary-foreground" />
            </div>
            <div className="text-center">
              <CardTitle className="text-2xl">Admin Portal</CardTitle>
              <CardDescription className="mt-2">
                Secure access for administrators only
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={e =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="admin@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  value={formData.password}
                  onChange={e =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <div className="mt-6 space-y-2 rounded-lg bg-muted p-4">
              <p className="text-center text-sm text-muted-foreground">
                <Shield className="mr-1 inline h-4 w-4" />
                This portal is restricted to authorized administrators
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
